/**
 * Directory hooks — option lists for the LookupField comboboxes.
 *
 * Consultants: `GET /v1/users/directory` — the caller's own dealership's
 * active staff, open to every role — is tried first, so every user sees the
 * same full list. On an older backend without it we fall back.
 *
 * (Before the directory: `GET /v1/users` is @PreAuthorize SUPER_ADMIN, so a
 * sales manager or BDC agent gets a 403. We therefore:)
 *   1. try the full user list (works for super-admins — gives names + roles);
 *   2. on failure, fall back to the consultants who have an active diary
 *      schedule (`GET /v1/diary/schedules`, open to all roles), and hydrate
 *      each one's name via `GET /v1/users/{id}` — that endpoint carries no
 *      @PreAuthorize, so it's readable by any authenticated user.
 *
 * The schedule-backed set is arguably the *correct* one anyway: DiarySlotService
 * only emits bookable slots for consultants with an active schedule, so a
 * consultant missing from it has no diary to book into.
 *
 * Customers and leads have no search endpoint — we pull a page and filter
 * client-side inside LookupField.
 */
import { useEffect, useState } from 'react';
import {
  usersService, customersService, leadsService, appointmentsService, vehiclesService,
} from './services';

const PAGE = 200;

const unwrapList = (r) =>
  r?.data?.content
  ?? r?.content
  ?? (Array.isArray(r?.data) ? r.data : null)
  ?? (Array.isArray(r) ? r : []);

const fullName = (u) =>
  [u?.firstName, u?.lastName].filter(Boolean).join(' ')
  || u?.email
  || (u?.id ? `User ${String(u.id).slice(0, 8)}…` : 'Unknown');

const prettyRole = (roles) => {
  const r = (roles || []).find(x => x !== 'SUPER_ADMIN') || (roles || [])[0];
  return r ? r.replace(/_/g, ' ').toLowerCase() : null;
};

/* ── Consultants ── */
export function useConsultantOptions() {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const toOption = (u) => ({
      id: u.id,
      primary: fullName(u),
      secondary: u.email || String(u.id).slice(0, 8) + '…',
      meta: prettyRole(u.roles),
      search: `${fullName(u)} ${u.email || ''} ${(u.roles || []).join(' ')} ${u.id}`.toLowerCase(),
      inactive: u.status && u.status !== 'ACTIVE',
    });

    // Primary path: the staff directory — every role, own dealership only.
    const viaDirectory = async () => {
      const rows = await usersService.directory();
      const opts = (Array.isArray(rows) ? rows : [])
        .filter(u => u?.id && u.status !== 'INACTIVE')
        .map(toOption);
      if (!opts.length) throw new Error('empty directory');
      return opts;
    };

    // Next: the dealership-scoped user list. This is accessible to
    // DEALER_PRINCIPAL (and SUPER_ADMIN) — unlike the flat GET /v1/users, which
    // is SUPER_ADMIN-only and 403s for a dealer principal (that 403 is exactly
    // why this picker was empty).
    const viaDealership = async () => {
      const me = await usersService.me();
      const dealershipId = me?.dealershipId;
      if (dealershipId == null) throw new Error('no dealership on principal');
      const rows = await usersService.listByDealership(dealershipId, { page: 0, size: PAGE });
      const opts = (Array.isArray(rows) ? rows : [])
        .filter(u => u?.id && u.status !== 'INACTIVE')
        .map(toOption);
      if (!opts.length) throw new Error('empty dealership list');
      return opts;
    };

    // Super-admin flat list — only reachable by SUPER_ADMIN; kept as a
    // secondary path for that role.
    const viaUserList = async () => {
      const res = await usersService.list({ page: 0, size: PAGE });
      const rows = unwrapList(res);
      if (!rows.length) throw new Error('empty user list');
      return rows
        .filter(u => u?.id && u.status !== 'INACTIVE')
        .map(toOption);
    };

    const viaSchedules = async () => {
      // Consultants with a diary. Falls back to whoever already appears on an
      // appointment if no schedules are configured yet.
      let ids = [];
      try {
        const scheds = await appointmentsService.schedules();
        ids = (Array.isArray(scheds) ? scheds : [])
          .filter(s => s?.consultantId && s.active !== false)
          .map(s => s.consultantId);
      } catch (_) { /* fall through */ }

      // Plus whoever already appears on an appointment, and the caller — so a
      // consultant is never offered only the principal who owns the diary.
      try {
        const page = await appointmentsService.list({ page: 0, size: PAGE });
        ids = ids.concat(unwrapList(page).map(a => a?.consultantId).filter(Boolean));
      } catch (_) { /* fall through */ }
      try {
        const me = await usersService.me();
        const meId = (me?.data ?? me)?.id;
        if (meId) ids.push(meId);
      } catch (_) { /* fall through */ }

      const unique = [...new Set(ids)];
      if (!unique.length) return [];

      // Hydrate names. GET /v1/users/{id} is open to any authenticated user;
      // if one lookup fails we still show the consultant by short id.
      const users = await Promise.all(unique.map(id =>
        usersService.get(id)
          .then(r => r?.data ?? r)
          .catch(() => ({ id }))
      ));
      return users.filter(u => u?.id).map(toOption);
    };

    (async () => {
      setLoading(true);
      setError(null);

      // Try each strategy in order until one yields users. Roles map to paths:
      //   DEALER_PRINCIPAL / SUPER_ADMIN → viaDealership (or viaUserList)
      //   SALES_MANAGER / BDC_AGENT / consultant → viaSchedules (no user-list
      //     access, so derive bookable consultants from the diary/appointments)
      const strategies = [viaDirectory, viaDealership, viaUserList, viaSchedules];
      let rows = [];
      let lastErr = null;
      for (const strategy of strategies) {
        try {
          const result = await strategy();
          if (result && result.length) { rows = result; break; }
        } catch (err) {
          lastErr = err;
        }
      }

      if (!cancelled) {
        if (!rows.length && lastErr) setError('Could not load consultants');
        rows.sort((a, b) => a.primary.localeCompare(b.primary));
        setOptions(rows);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return { options, loading, error };
}

/* ── Customers ── */
export function useCustomerOptions() {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    customersService.list({ page: 0, size: PAGE })
      .then((res) => {
        if (cancelled) return;
        const rows = unwrapList(res).filter(c => c?.id).map((c) => {
          const name = [c.firstName, c.lastName].filter(Boolean).join(' ')
            || c.email || `Customer ${String(c.id).slice(0, 8)}…`;
          const contact = [c.phoneE164, c.email].filter(Boolean).join(' · ');
          return {
            id: c.id,
            primary: name,
            secondary: contact || String(c.id).slice(0, 8) + '…',
            meta: c.vip ? 'VIP' : null,
            search: `${name} ${c.phoneE164 || ''} ${c.email || ''} ${c.id}`.toLowerCase(),
          };
        });
        rows.sort((a, b) => a.primary.localeCompare(b.primary));
        setOptions(rows);
      })
      .catch(() => { /* picker shows "no matches" */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { options, loading };
}

/* ── Vehicles (stock) ── */
/**
 * @param condition  restrict to one Vehicle.Condition ('NEW' | 'DEMO' | 'USED'),
 *                    e.g. the Test Drive picker passes 'DEMO' so a dealer isn't
 *                    offered a customer's new-car stock to put test-drive km on.
 *                    Left undefined, every condition is returned — same as before.
 */
export function useVehicleOptions({ availableOnly = false, condition } = {}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const params = { page: 0, limit: PAGE };
    if (condition) params.condition = condition;
    vehiclesService.list(params)
      .then((res) => {
        if (cancelled) return;
        const rows = unwrapList(res)
          .filter(v => v?.id)
          .filter(v => !availableOnly || !v.stockStatus || v.stockStatus === 'AVAILABLE')
          .map((v) => {
            const name = [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ')
              || `Vehicle ${String(v.id).slice(0, 8)}…`;
            const bits = [
              v.dmsStockId ? `Stock ${v.dmsStockId}` : null,
              v.vin ? `VIN ${v.vin}` : null,
              v.km != null ? `${Number(v.km).toLocaleString()} km` : null,
            ].filter(Boolean);
            // Condition first (new/demo/used — what it IS), stock status
            // second (what state it's in) — same ordering logic the
            // Appointments calendar uses for type vs. status.
            const metaBits = [
              v.condition ? v.condition.toLowerCase() : null,
              v.stockStatus ? v.stockStatus.replace(/_/g, ' ').toLowerCase() : null,
            ].filter(Boolean);
            return {
              id: v.id,
              primary: name,
              secondary: bits.join(' · ') || String(v.id).slice(0, 8) + '…',
              meta: metaBits.join(' · ') || null,
              search: `${name} ${v.dmsStockId || ''} ${v.vin || ''} ${v.condition || ''} ${v.stockStatus || ''} ${v.id}`.toLowerCase(),
            };
          });
        rows.sort((a, b) => a.primary.localeCompare(b.primary));
        setOptions(rows);
      })
      .catch(() => { /* picker shows "no matches" */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [availableOnly, condition]);

  return { options, loading };
}

/* ── Leads ── */
export function useLeadOptions(customersById) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    leadsService.list({ page: 0, size: PAGE })
      .then((res) => {
        if (cancelled) return;
        const rows = unwrapList(res).filter(l => l?.id).map((l) => {
          const vehicle = [l.vehicleMakeRequested, l.vehicleModelRequested]
            .filter(Boolean).join(' ');
          const who = customersById?.get?.(l.customerId)?.primary;
          const primary = who || vehicle || `Lead ${String(l.id).slice(0, 8)}…`;
          const secondary = [vehicle && who ? vehicle : null, l.source]
            .filter(Boolean).join(' · ') || String(l.id).slice(0, 8) + '…';
          return {
            id: l.id,
            customerId: l.customerId || null,
            primary,
            secondary,
            meta: l.stage ? l.stage.replace(/_/g, ' ').toLowerCase() : null,
            search: `${primary} ${vehicle} ${l.source || ''} ${l.stage || ''} ${l.id}`.toLowerCase(),
          };
        });
        setOptions(rows);
      })
      .catch(() => { /* picker shows "no matches" */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customersById]);

  return { options, loading };
}
